import {useCallback, useEffect, useState, type Dispatch, type SetStateAction} from "react";
import type {Annotation, GridPoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {imageToGrid} from "@/dungeons/geometry/Coordinates.ts";
import type {DungeonViewport} from "@/dungeons/editor/shared/DungeonViewport.ts";
import type {GeometryDocumentUpdate} from "@/dungeons/editor/shared/useLevelGeometryDocument.ts";
import {changeAnnotation, createAnnotation, removeAnnotation} from "./AnnotationOperations.ts";

export default function useDungeonAnnotations({document, updateDocument, setViewport}: {
    document: LevelGeometryDocument;
    updateDocument: (update: GeometryDocumentUpdate) => void;
    setViewport: Dispatch<SetStateAction<DungeonViewport | undefined>>;
}) {
    const [newKind, setNewKind] = useState<Annotation["kind"]>("ROOM");
    const [selectedId, setSelectedId] = useState<string>();
    const [draft, setDraft] = useState<Annotation>();
    const [editing, setEditing] = useState(false);
    const [dragging, setDragging] = useState(false);
    const annotations = draft ? [...document.annotations, draft] : document.annotations;
    const selected = annotations.find(annotation => annotation.id === selectedId);

    const close = useCallback(() => {
        setSelectedId(undefined);
        setDraft(undefined);
        setEditing(false);
    }, []);
    const place = (imagePoint: GridPoint) => {
        const annotation = createAnnotation(imageToGrid(imagePoint, document.registration), newKind);
        setDraft(annotation);
        setSelectedId(annotation.id);
        setEditing(true);
    };
    const select = (id: string) => {
        if (draft?.id === id) return;
        setDraft(undefined);
        setSelectedId(id);
        setEditing(false);
    };
    const focus = (annotation: Annotation) => {
        select(annotation.id);
        setViewport(current => ({center: annotation.position, pixelsPerCell: current?.pixelsPerCell ?? 80}));
    };
    const save = (annotation: Annotation) => {
        updateDocument(current => draft?.id === annotation.id
            ? {...current, annotations: [...current.annotations, annotation]}
            : changeAnnotation(current, annotation.id, annotation));
        setDraft(undefined);
        setEditing(false);
    };
    const remove = (id: string) => {
        updateDocument(current => removeAnnotation(current, id));
        close();
    };
    const move = (id: string, imagePoint: GridPoint) => {
        updateDocument(current => changeAnnotation(current, id,
            {position: imageToGrid(imagePoint, current.registration)}));
        setDragging(false);
    };
    useEffect(() => {
        if (!selectedId) return;
        const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") close(); };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [selectedId, close]);

    return {
        annotations, selected, selectedId, draft, editing, dragging, newKind, setNewKind,
        setEditing, setDragging, close, place, select, focus, save, remove, move,
    };
}

export type DungeonAnnotationsController = ReturnType<typeof useDungeonAnnotations>;
